import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DatepickerHelper } from '../../../../shared/validators/date-picker-form-validators';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { SelectOption } from '@models/ui-models/select-options';
import { companyNameValidator } from '../../../../shared/validators/compny-name.validator';
import { PaymentMethod, paymentMethodOptions } from '../../../models/company/company-change-name.model';
import { Form410FTransaction, Form410FStepsEnum } from '../../../models/company/form-410f-transaction.model';
import {
  reservationNumberCustomErrors,
  reservationNumberValidator
} from "../../../../shared/validators/reservation-number.validator";
import { InputComponent } from "@shared/controls/input/input.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { TextareaComponent } from "@shared/controls/textarea/textarea.component";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";

@Component({
  selector: 'app-form410f',
  standalone: true,
  templateUrl: './form-410f-transaction.component.html',
  imports: [
    ReactiveFormsModule,
    InputComponent,
    RadioComponent,
    DatePickerComponent,
    TextareaComponent,
    StepperFormComponent,
    StepperFormDescriptionComponent,
  ],
  styleUrl: '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
})
export class Form410FTransactionComponent extends BaseStepperFormComponent<Form410FStepsEnum, Form410FTransaction> implements OnInit {
  override readonly StepsEnum = Form410FStepsEnum;
  readonly paymentMethodOptions: SelectOption[] = paymentMethodOptions;
  readonly reservationNumberCustomErrors = reservationNumberCustomErrors;
  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;

  override stepperForm = new FormGroup({
    [Form410FStepsEnum.FormDetails]: new FormGroup({}),
    [Form410FStepsEnum.CompanyNameDetails]: new FormGroup({
      proposedCompanyName: new FormControl<string>('', [Validators.required, Validators.minLength(7), companyNameValidator()]),
      reservationDocumentNumber: new FormControl<string | null>(null, [Validators.required, reservationNumberValidator]),
      existingExpiryDate: new FormControl<Date | null>(null, [Validators.required]),
      paymentMethod: new FormControl<PaymentMethod>(PaymentMethod.INV, [Validators.required]),
    }),
    [Form410FStepsEnum.ExtensionDetails]: new FormGroup({
      extensionReason: new FormControl<string>('', [Validators.required]),
    }),
  });

  constructor() {
    super();
    this.setupSteps(Form410FStepsEnum);
    this.redirectAfterSubmit = true;
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDateOfEstablishmentMinDate(this.companyChangeData.dateOfEstablishment);
    this.maxDate = DatepickerHelper.getNextNYearsStruct(10);
    this.stepperForm.controls[Form410FStepsEnum.CompanyNameDetails].controls.reservationDocumentNumber.setValue(this.companyChangeData.documentId ?? null);
    this.setupChange();
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as Form410FTransaction);
  }

  override setupChange(change: Form410FTransaction = this.formModel) {
    if (!this.isEdit && change === this.formModel) {
      return;
    }

    this.stepperForm.patchValue({
      [Form410FStepsEnum.CompanyNameDetails]: { ...change },
      [Form410FStepsEnum.ExtensionDetails]: { ...change }
    });
  }

  override buildDocument(): Document | null {
    const companyNameDetailsFormValue = this.stepperForm.controls[Form410FStepsEnum.CompanyNameDetails].value;
    const extensionDetailsFormValue = this.stepperForm.controls[Form410FStepsEnum.ExtensionDetails].value;
    const description = companyNameDetailsFormValue.proposedCompanyName
      ? 'Extend the company name reservation to ' + companyNameDetailsFormValue.proposedCompanyName
      : '';

    const changes = new Form410FTransaction({
      ...companyNameDetailsFormValue as Partial<Form410FTransaction>,
      ...extensionDetailsFormValue as Partial<Form410FTransaction>,
      changeDate: DatepickerHelper.getToday(),
      existingExpiryDate: companyNameDetailsFormValue.existingExpiryDate!,
      description
    });

    try {
      return new Document({
        changes: [changes],
        entityId: this.companyChangeData.entityId,
        type: 'c:410f',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }
}

